import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { usePerformanceGroups } from '@/hooks/usePerformanceGroups';
import { usePerformanceChart } from '@/hooks/usePerformanceChart';
import { GroupList } from '@/components/admin/performance-analyze/GroupList';
import { GroupFormDialog } from '@/components/admin/performance-analyze/GroupFormDialog';
import { BulkActions } from '@/components/admin/performance-analyze/BulkActions';
import { ChartFilters } from '@/components/admin/performance-analyze/ChartFilters';
import { PerformanceChart } from '@/components/admin/performance-analyze/PerformanceChart';
import { PerformanceGroup } from '@/types/performance-analyze';
import { format, subDays } from 'date-fns';

export default function PerformanceAnalyzePage() {
  const [activeTab, setActiveTab] = useState('groups');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PerformanceGroup | null>(null);

  // Chart filters
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [windowSize, setWindowSize] = useState(7);
  const [startDate, setStartDate] = useState<Date | undefined>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [appliedFilters, setAppliedFilters] = useState<{
    groupIds: string[];
    windowSize: number;
    startDate?: string;
    endDate?: string;
  } | null>(null);

  const { groups, isLoading, createGroup, updateGroup, deleteGroup, refetch } = usePerformanceGroups();

  const { data: chartData, isLoading: chartLoading } = usePerformanceChart(
    appliedFilters?.groupIds || [],
    appliedFilters?.windowSize || 7,
    appliedFilters?.startDate,
    appliedFilters?.endDate
  );

  const handleCreate = () => {
    setEditingGroup(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (group: PerformanceGroup) => {
    setEditingGroup(group);
    setFormDialogOpen(true);
  };

  const handleCopy = (group: PerformanceGroup) => {
    setEditingGroup({
      ...group,
      id: '', // Will be generated
      symbol: group.symbol + '_COPY'
    } as PerformanceGroup);
    setFormDialogOpen(true);
  };

  const handleSubmit = (data: Partial<PerformanceGroup>) => {
    if (editingGroup && editingGroup.id) {
      updateGroup.mutate({ id: editingGroup.id, ...data });
    } else {
      createGroup.mutate(data as any);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      groupIds: selectedGroupIds,
      windowSize,
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Performance Analyze</h1>
          <p className="text-muted-foreground">
            Compare rolling winrate across bot groups
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="groups">Group Management</TabsTrigger>
          <TabsTrigger value="chart">Chart Visualization</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-between items-center">
            <BulkActions groups={groups} onImport={refetch} />
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading groups...</div>
          ) : (
            <GroupList
              groups={groups}
              onEdit={handleEdit}
              onDelete={(id) => deleteGroup.mutate(id)}
              onCopy={handleCopy}
            />
          )}
        </TabsContent>

        <TabsContent value="chart" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <ChartFilters
                groups={groups}
                selectedGroupIds={selectedGroupIds}
                onGroupsChange={setSelectedGroupIds}
                windowSize={windowSize}
                onWindowSizeChange={setWindowSize}
                startDate={startDate}
                endDate={endDate}
                onDateRangeChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                onApply={handleApplyFilters}
              />
            </div>

            <div className="lg:col-span-3">
              {chartLoading ? (
                <div className="h-[500px] flex items-center justify-center border rounded-lg">
                  <p className="text-muted-foreground">Loading chart data...</p>
                </div>
              ) : (
                <PerformanceChart
                  data={chartData?.chartData || []}
                  groups={chartData?.groups || []}
                />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <GroupFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        group={editingGroup}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
